import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  closestCorners,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowLeft, EllipsisVertical, Eye, GripVertical, Plus } from 'lucide-react';
import { useResponsiveDrag } from 'services/useResponsiveDrag';
import useOverlayHistory from 'services/useOverlayHistory';
import SpinnerLoad from '../moduleComponents/SpinnerLoad';
import ConfirmModal from '../moduleComponents/ConfirmModal';
import SortableProjectCard from './viewComponents/SortableProjectCard';
import ModalColumnForm from './viewComponents/ModalColumnForm';
import DrawerCardForm, { columnPkFromCard } from './viewComponents/DrawerCardForm';
import {
  fetchBoard,
  createColumn,
  updateColumn,
  deleteColumn,
  createCard,
  updateCard,
  deleteCard,
  reorderCards,
  reorderColumns,
} from '../moduleServices/planner_projects_api';

/** Fixed Kanban column width (desktop); matches a compact lane, not the full viewport. */
const COL_WIDTH = 'w-full sm:w-[272px] sm:min-w-[272px] sm:max-w-[272px] sm:shrink-0';

/** Square add-column control, sitting on the project header row. */
const ADD_COLUMN_BTN_CLASS =
  'flex shrink-0 items-center justify-center h-10 w-10 rounded-lg border border-gray-200 bg-gray-50 text-blue-600 shadow-sm hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 active:scale-[0.98] transition';

function BoardViewOption({ checked, label, onClick }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onClick}
      className="flex w-full items-center justify-between gap-3 px-2.5 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50"
    >
      <span>{label}</span>
      <span
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-blue-600' : 'bg-gray-300'
        }`}
        aria-hidden
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </span>
    </button>
  );
}

function BoardViewOptions({
  showDescription,
  onDescriptionChange,
  showEmptyNoColumn,
  onEmptyNoColumnChange,
  canvasMaxWidth,
  onCanvasMaxWidthChange,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label="View options"
        aria-expanded={open}
        title="View options"
        className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50 hover:border-gray-300 active:scale-[0.98] transition"
      >
        <Eye className="h-5 w-5" strokeWidth={2} />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-60 rounded-md border border-gray-200 bg-white py-1 shadow">
          <BoardViewOption
            checked={showDescription}
            label="Show description"
            onClick={() => onDescriptionChange(!showDescription)}
          />
          <BoardViewOption
            checked={showEmptyNoColumn}
            label="Show empty No column"
            onClick={() => onEmptyNoColumnChange(!showEmptyNoColumn)}
          />
          <BoardViewOption
            checked={canvasMaxWidth}
            label="Max width"
            onClick={() => onCanvasMaxWidthChange(!canvasMaxWidth)}
          />
        </div>
      )}
    </div>
  );
}

function BoardHorizontalScroll({ watchKey, className, children }) {
  const scrollerRef = useRef(null);
  const trackRef = useRef(null);
  const dragRef = useRef(null);
  const [metrics, setMetrics] = useState({ scrollLeft: 0, scrollWidth: 1, clientWidth: 1 });
  const [trackWidth, setTrackWidth] = useState(0);

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setMetrics((prev) => {
      if (
        prev.scrollLeft === el.scrollLeft
        && prev.scrollWidth === el.scrollWidth
        && prev.clientWidth === el.clientWidth
      ) return prev;
      return { scrollLeft: el.scrollLeft, scrollWidth: el.scrollWidth, clientWidth: el.clientWidth };
    });
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    Array.from(el.children).forEach((child) => ro.observe(child));
    el.addEventListener('scroll', measure, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', measure);
    };
  }, [measure, watchKey]);

  const maxScroll = Math.max(0, metrics.scrollWidth - metrics.clientWidth);
  const overflow = maxScroll > 1;

  useLayoutEffect(() => {
    if (!overflow || !trackRef.current) return;
    setTrackWidth(trackRef.current.clientWidth);
  }, [overflow, metrics.clientWidth, watchKey]);

  const thumbWidth = overflow && trackWidth
    ? Math.max(40, (metrics.clientWidth / metrics.scrollWidth) * trackWidth)
    : 0;
  const thumbTravel = Math.max(0, trackWidth - thumbWidth);
  const thumbLeft = maxScroll > 0 ? (metrics.scrollLeft / maxScroll) * thumbTravel : 0;

  const scrollToThumbLeft = (nextLeft) => {
    const el = scrollerRef.current;
    if (!el || thumbTravel <= 0) return;
    const clamped = Math.min(thumbTravel, Math.max(0, nextLeft));
    el.scrollLeft = (clamped / thumbTravel) * maxScroll;
  };

  const onThumbPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = { pointerX: event.clientX, thumbLeft };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onThumbPointerMove = (event) => {
    if (!dragRef.current) return;
    const dx = event.clientX - dragRef.current.pointerX;
    scrollToThumbLeft(dragRef.current.thumbLeft + dx);
  };

  const onThumbPointerUp = () => {
    dragRef.current = null;
  };

  const onTrackPointerDown = (event) => {
    if (event.target !== event.currentTarget) return;
    const rect = event.currentTarget.getBoundingClientRect();
    scrollToThumbLeft(event.clientX - rect.left - thumbWidth / 2);
  };

  return (
    <>
      {overflow ? (
        <div className="hidden sm:block shrink-0 px-2 mb-2">
          <div
            ref={trackRef}
            className="relative h-2 rounded-full bg-slate-200/80 cursor-pointer"
            onPointerDown={onTrackPointerDown}
          >
            <div
              role="scrollbar"
              aria-orientation="horizontal"
              aria-valuenow={Math.round(metrics.scrollLeft)}
              aria-valuemin={0}
              aria-valuemax={Math.round(maxScroll)}
              className="absolute top-0 h-2 rounded-full bg-slate-400/85 shadow-[inset_0_1px_0_rgb(255_255_255_/_0.45)] cursor-grab active:cursor-grabbing hover:bg-slate-500/90"
              style={{ width: thumbWidth, transform: `translateX(${thumbLeft}px)` }}
              onPointerDown={onThumbPointerDown}
              onPointerMove={onThumbPointerMove}
              onPointerUp={onThumbPointerUp}
              onPointerCancel={onThumbPointerUp}
            />
          </div>
        </div>
      ) : null}
      <div ref={scrollerRef} className={className}>
        {children}
      </div>
    </>
  );
}

function parseCardKey(prefixed) {
  const raw = String(prefixed).replace(/^card-/, '');
  if (raw.startsWith('temp_')) return raw;
  return Number.parseInt(raw, 10);
}

function laneKey(columnId) {
  return columnId == null || columnId === '' ? 'orphan' : String(columnId);
}

function placeCardInLane(containers, cardKey, columnId) {
  const next = cloneContainers(containers);
  Object.keys(next).forEach((key) => {
    next[key] = next[key].filter((id) => id !== cardKey);
  });
  const dest = laneKey(columnId);
  if (!next[dest]) next[dest] = [];
  if (!next[dest].includes(cardKey)) next[dest].push(cardKey);
  return next;
}

function parseColDropId(overId) {
  const s = String(overId);
  if (s === 'col-orphan') return 'orphan';
  if (s.startsWith('col-')) return s.slice(4);
  return null;
}

function findCardContainer(containers, activeId) {
  const id = String(activeId);
  if (!id.startsWith('card-')) return null;
  for (const key of Object.keys(containers)) {
    if (containers[key].includes(id)) return key;
  }
  return null;
}

function cloneContainers(containers) {
  const next = {};
  Object.keys(containers).forEach((k) => {
    next[k] = [...containers[k]];
  });
  return next;
}

function buildReorderUpdates(nextContainers) {
  const updates = [];
  const orphan = nextContainers.orphan || [];
  orphan.forEach((prefixed, idx) => {
    updates.push({ id: parseCardKey(prefixed), column_id: null, order: idx });
  });
  Object.keys(nextContainers).forEach((key) => {
    if (key === 'orphan') return;
    nextContainers[key].forEach((prefixed, idx) => {
      const columnId = String(key).startsWith('temp_') ? key : Number.parseInt(key, 10);
      updates.push({ id: parseCardKey(prefixed), column_id: columnId, order: idx });
    });
  });
  return updates;
}

function boardToState(boardPayload) {
  const cardsById = {};
  const ingest = (c) => {
    cardsById[c.id] = { ...c };
  };
  (boardPayload.orphan_cards || []).forEach(ingest);
  (boardPayload.columns || []).forEach((col) => (col.cards || []).forEach(ingest));

  const columnsOrdered = [...(boardPayload.columns || [])].sort((a, b) => a.order - b.order);

  const containers = {
    orphan: (boardPayload.orphan_cards || [])
      .sort((a, b) => a.order - b.order)
      .map((c) => `card-${c.id}`),
  };
  columnsOrdered.forEach((col) => {
    containers[String(col.id)] = (col.cards || [])
      .sort((a, b) => a.order - b.order)
      .map((c) => `card-${c.id}`);
  });

  return { columnsOrdered, containers, cardsById };
}

/** Drop area styled like Planner To-Do DropZone (gray lane + padding) */
function ColumnDropArea({ colKey, children, padBottomForFab = false }) {
  const dropId = colKey === 'orphan' ? 'col-orphan' : `col-${colKey}`;
  const { setNodeRef, isOver } = useDroppable({ id: dropId });
  return (
    <div
      ref={setNodeRef}
      className={`kanban-column-scroll flex-1 min-h-[220px] sm:min-h-0 overflow-y-auto overflow-x-hidden transition-colors duration-200 p-2 ${
        isOver ? 'bg-blue-50/90 ring-1 ring-inset ring-blue-200' : 'bg-gray-100'
      }`}
    >
      <div className={`flex flex-col gap-1 min-h-[120px] ${padBottomForFab ? 'pb-14' : ''}`}>{children}</div>
    </div>
  );
}

function LaneAddCardFab({ onClick, label = 'Add card' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="absolute bottom-3 right-3 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg shadow-blue-600/30 ring-2 ring-white hover:bg-blue-700 active:scale-95 transition-opacity duration-150 opacity-100 pointer-events-auto sm:opacity-0 sm:pointer-events-none sm:group-hover:opacity-100 sm:group-hover:pointer-events-auto sm:group-focus-within:opacity-100 sm:group-focus-within:pointer-events-auto"
    >
      <Plus className="h-6 w-6" strokeWidth={2} />
    </button>
  );
}

function ColumnHeaderMenu({ column, onEdit, onDeleteKeepCards, onDeleteWithCards }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="relative shrink-0" ref={rootRef}>
      <button
        type="button"
        className="p-1 rounded-lg hover:bg-gray-200 text-gray-600"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Column actions"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <EllipsisVertical className="h-5 w-5" strokeWidth={2} />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-40 min-w-[220px] rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            className="block w-full px-4 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50"
            onClick={() => {
              setOpen(false);
              onEdit(column);
            }}
          >
            Edit
          </button>
          <button
            type="button"
            role="menuitem"
            className="block w-full px-4 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50"
            onClick={() => {
              setOpen(false);
              onDeleteKeepCards(column);
            }}
          >
            Delete column
          </button>
          <button
            type="button"
            role="menuitem"
            className="block w-full px-4 py-2.5 text-left text-sm text-red-700 hover:bg-red-50"
            onClick={() => {
              setOpen(false);
              onDeleteWithCards(column);
            }}
          >
            Delete column and cards
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * One Kanban column: header + card lane share the same shell (rounded-2xl shadow like Planner cards).
 * Whole shell is the sortable node for reordering columns; drag handle only activates column drag.
 */
function SortableBoardColumn({ hdrId, column, onEditColumn, onDeleteColumn, cardIds, cardsById, onCardClick, onOpenNewCard, showDescription }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: hdrId });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.92 : 1,
    zIndex: isDragging ? 20 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex flex-col sm:h-full sm:min-h-0 ${COL_WIDTH} rounded-2xl bg-white shadow overflow-hidden border border-gray-200`}
    >
      <div className="flex items-center gap-1 shrink-0 px-2 py-2 bg-gray-50 border-b border-gray-200">
        <button
          type="button"
          className="p-1.5 text-gray-400 cursor-grab active:cursor-grabbing rounded-lg hover:bg-gray-200"
          {...attributes}
          {...listeners}
          aria-label="Reorder column"
        >
          <GripVertical className="h-4 w-4" strokeWidth={2} />
        </button>
        <span className="flex-1 font-semibold text-sm text-gray-800 truncate">{column.name}</span>
        <ColumnHeaderMenu
          column={column}
          onEdit={onEditColumn}
          onDeleteKeepCards={(c) => onDeleteColumn(c, false)}
          onDeleteWithCards={(c) => onDeleteColumn(c, true)}
        />
      </div>
      <div className="relative flex-1 flex flex-col min-h-0">
        <ColumnDropArea colKey={String(column.id)} padBottomForFab>
          <SortableContext items={cardIds} strategy={verticalListSortingStrategy}>
            {cardIds.map((sid) => {
              const c = cardsById[parseCardKey(sid)];
              if (!c) return null;
              return (
                <SortableProjectCard key={sid} id={sid} card={c} showDescription={showDescription} onClick={(card) => onCardClick(card, column.id)} />
              );
            })}
          </SortableContext>
        </ColumnDropArea>
        <LaneAddCardFab onClick={() => onOpenNewCard(column.id)} />
      </div>
    </div>
  );
}

function BoardOrphanColumn({ orphanSortableIds, cardsById, onOpenNewCard, onCardClick, showDescription }) {
  return (
    <div
      className={`group flex flex-col sm:h-full sm:min-h-0 ${COL_WIDTH} rounded-2xl bg-white shadow overflow-hidden border border-dashed border-gray-300`}
    >
      <div className="flex items-center gap-2 shrink-0 px-3 py-2 bg-gray-50 border-b border-gray-200">
        <span className="font-semibold text-sm text-gray-800 truncate">No column</span>
      </div>
      <div className="relative flex-1 flex flex-col min-h-0">
        <ColumnDropArea colKey="orphan" padBottomForFab>
          <SortableContext items={orphanSortableIds} strategy={verticalListSortingStrategy}>
            {orphanSortableIds.map((sid) => {
              const c = cardsById[parseCardKey(sid)];
              if (!c) return null;
              return <SortableProjectCard key={sid} id={sid} card={c} showDescription={showDescription} onClick={(card) => onCardClick(card, null)} />;
            })}
          </SortableContext>
        </ColumnDropArea>
        <LaneAddCardFab onClick={() => onOpenNewCard(null)} />
      </div>
    </div>
  );
}

export default function ViewProjectsBoard({ projectSummary, onBack, onCanvasMaxWidthChange }) {
  const projectId = projectSummary.id;
  const [loading, setLoading] = useState(true);
  const [columnsOrdered, setColumnsOrdered] = useState([]);
  const [containers, setContainers] = useState({ orphan: [] });
  const [cardsById, setCardsById] = useState({});
  const [activeCardId, setActiveCardId] = useState(null);

  const [columnModal, setColumnModal] = useState({ open: false, column: null });
  const [columnSubmitting, setColumnSubmitting] = useState(false);
  const [cardModal, setCardModal] = useState({ open: false, card: null, defaultColumnId: null });
  const [cardSubmitting, setCardSubmitting] = useState(false);
  const [columnDelete, setColumnDelete] = useState(null);
  const [showDescription, setShowDescription] = useState(false);
  const [showEmptyNoColumn, setShowEmptyNoColumn] = useState(false);
  const [canvasMaxWidth, setCanvasMaxWidth] = useState(true);

  useEffect(() => {
    onCanvasMaxWidthChange?.(canvasMaxWidth);
  }, [canvasMaxWidth, onCanvasMaxWidthChange]);

  useEffect(() => () => onCanvasMaxWidthChange?.(true), [onCanvasMaxWidthChange]);

  const { activationDistance, activationDelay, activationTolerance, enableTouchGestures } = useResponsiveDrag();

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: activationDistance },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: activationDelay, tolerance: activationTolerance },
  });
  const sensors = useSensors(pointerSensor, ...(enableTouchGestures ? [touchSensor] : []));
  const requestCloseColumnDelete = useOverlayHistory(!!columnDelete, () => setColumnDelete(null), {
    stateKey: 'qappColumnDelete',
  });

  const columnHeaderIds = useMemo(() => columnsOrdered.map((c) => `colhdr-${c.id}`), [columnsOrdered]);

  const reloadBoard = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchBoard(projectId);
      const st = boardToState(data);
      setColumnsOrdered(st.columnsOrdered);
      setContainers(st.containers);
      setCardsById(st.cardsById);
    } catch (e) {
      console.error(e);
      toast.error('Could not load board.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    reloadBoard();
  }, [reloadBoard]);

  const persistCardOrder = async (nextContainers) => {
    try {
      await reorderCards(projectId, buildReorderUpdates(nextContainers));
    } catch (e) {
      console.error(e);
      toast.error('Could not save card order.');
      reloadBoard();
    }
  };

  const handleCardDragEnd = async (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    const activeContainer = findCardContainer(containers, activeId);
    let overContainer = findCardContainer(containers, overId);

    if (!activeContainer) return;

    if (!overContainer) {
      const parsed = parseColDropId(overId);
      if (parsed != null) overContainer = parsed;
    }

    if (!overContainer) return;

    let next;

    if (activeContainer === overContainer) {
      const items = [...containers[activeContainer]];
      const oldIdx = items.indexOf(activeId);
      let newIdx = items.indexOf(overId);
      if (oldIdx === -1) return;
      if (newIdx === -1) {
        if (overId.startsWith('col-')) newIdx = items.length - 1;
        else return;
      }
      next = cloneContainers(containers);
      next[activeContainer] = arrayMove(next[activeContainer], oldIdx, newIdx);
    } else {
      next = cloneContainers(containers);
      next[activeContainer] = next[activeContainer].filter((id) => id !== activeId);
      const dest = [...next[overContainer]];
      if (overId.startsWith('card-')) {
        const idx = dest.indexOf(overId);
        if (idx === -1) dest.push(activeId);
        else dest.splice(idx, 0, activeId);
      } else {
        dest.push(activeId);
      }
      next[overContainer] = dest;
    }

    setContainers(next);
    await persistCardOrder(next);
  };

  const handleColumnDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = columnHeaderIds.indexOf(active.id);
    const newIndex = columnHeaderIds.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(columnsOrdered, oldIndex, newIndex);
    setColumnsOrdered(newOrder);
    try {
      await reorderColumns(
        projectId,
        newOrder.map((c, i) => ({ id: c.id, order: i }))
      );
    } catch (e) {
      console.error(e);
      toast.error('Could not reorder columns.');
      reloadBoard();
    }
  };

  const handleUnifiedDragEnd = async (event) => {
    const aid = String(event.active?.id ?? '');
    setActiveCardId(null);
    if (aid.startsWith('colhdr-')) {
      await handleColumnDragEnd(event);
    } else if (aid.startsWith('card-')) {
      await handleCardDragEnd(event);
    }
  };

  const openNewColumn = () => setColumnModal({ open: true, column: null });
  const openEditColumn = (col) => setColumnModal({ open: true, column: col });

  const submitColumn = async ({ name }) => {
    setColumnSubmitting(true);
    try {
      if (columnModal.column) {
        const updated = await updateColumn(columnModal.column.id, { name, project: projectId });
        setColumnsOrdered((prev) => prev.map((col) => (col.id === updated.id ? { ...col, ...updated } : col)));
        toast.success('Column updated');
      } else {
        const created = await createColumn({
          project: projectId,
          name,
          order: columnsOrdered.length,
        });
        setColumnsOrdered((prev) => [...prev, created].sort((a, b) => a.order - b.order));
        setContainers((prev) => ({ ...prev, [String(created.id)]: prev[String(created.id)] || [] }));
        toast.success('Column created');
      }
      setColumnModal({ open: false, column: null });
    } catch (e) {
      console.error(e);
      toast.error('Could not save column.');
    } finally {
      setColumnSubmitting(false);
    }
  };

  const deleteColumnFromMenu = (col, purgeCards) => {
    if (!col) return;
    setColumnDelete({ column: col, purgeCards });
  };

  const confirmDeleteColumn = async () => {
    if (!columnDelete) return;
    const { column: col, purgeCards } = columnDelete;
    const key = String(col.id);
    try {
      await deleteColumn(col.id, purgeCards);
      const removedIds = containers[key] || [];
      setColumnsOrdered((prev) => prev.filter((item) => item.id !== col.id));
      setContainers((prev) => {
        const next = cloneContainers(prev);
        const ids = next[key] || [];
        delete next[key];
        if (!purgeCards) next.orphan = [...(next.orphan || []), ...ids];
        return next;
      });
      if (purgeCards) {
        setCardsById((prev) => {
          const next = { ...prev };
          removedIds.forEach((sid) => {
            delete next[parseCardKey(sid)];
          });
          return next;
        });
      }
      toast.success('Column deleted');
      setColumnDelete(null);
    } catch (e) {
      console.error(e);
      toast.error('Could not delete column.');
    }
  };

  const openNewCard = (columnId) =>
    setCardModal({ open: true, card: null, defaultColumnId: columnId });

  const openEditCard = (card, laneColumnId = null) =>
    setCardModal({ open: true, card, defaultColumnId: laneColumnId });

  const submitCard = async (payload) => {
    setCardSubmitting(true);
    try {
      if (cardModal.card) {
        const prevColumn = columnPkFromCard(cardModal.card);
        const nextColumn = payload.column;
        const columnChanged = String(prevColumn ?? '') !== String(nextColumn ?? '');
        const body = { ...payload };
        if (columnChanged) {
          const dest = nextColumn == null ? containers.orphan || [] : containers[String(nextColumn)] || [];
          body.order = dest.length;
        }
        const updated = await updateCard(cardModal.card.id, body);
        const cardKey = `card-${cardModal.card.id}`;
        setCardsById((prev) => ({ ...prev, [updated.id]: { ...prev[updated.id], ...updated } }));
        if (columnChanged) {
          setContainers((prev) => placeCardInLane(prev, cardKey, updated.column));
        }
        toast.success('Card updated');
      } else {
        const maxOrderLane =
          payload.column == null
            ? containers.orphan.length
            : (containers[String(payload.column)] || []).length;
        const created = await createCard({ ...payload, order: maxOrderLane });
        const cardKey = `card-${created.id}`;
        setCardsById((prev) => ({ ...prev, [created.id]: created }));
        setContainers((prev) => placeCardInLane(prev, cardKey, created.column));
        toast.success('Card created');
      }
      setCardModal({ open: false, card: null, defaultColumnId: null });
    } catch (e) {
      console.error(e);
      toast.error('Could not save card.');
    } finally {
      setCardSubmitting(false);
    }
  };

  const handleDeleteCard = async () => {
    if (!cardModal.card) return;
    const cardId = cardModal.card.id;
    const cardKey = `card-${cardId}`;
    setCardSubmitting(true);
    try {
      await deleteCard(cardId);
      setCardsById((prev) => {
        const next = { ...prev };
        delete next[cardId];
        return next;
      });
      setContainers((prev) => {
        const next = cloneContainers(prev);
        Object.keys(next).forEach((key) => {
          next[key] = next[key].filter((id) => id !== cardKey);
        });
        return next;
      });
      toast.success('Card deleted');
      setCardModal({ open: false, card: null, defaultColumnId: null });
    } catch (e) {
      console.error(e);
      toast.error('Could not delete card.');
    } finally {
      setCardSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="py-12">
        <SpinnerLoad />
      </div>
    );
  }

  const orphanSortableIds = containers.orphan || [];
  const showOrphanColumn = orphanSortableIds.length > 0 || showEmptyNoColumn;

  return (
    <div className="flex flex-col sm:h-[calc(100dvh-10rem)] sm:min-h-0">
      <div className="flex items-center gap-2 mb-4 px-1 shrink-0">
        <button type="button" onClick={onBack} className="p-2 rounded-full hover:bg-gray-200 text-gray-700">
          <ArrowLeft className="h-6 w-6" strokeWidth={2} />
        </button>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-gray-900 truncate">{projectSummary.name}</h2>
          {projectSummary.description?.trim() ? (
            <p className="text-xs text-gray-500 whitespace-pre-wrap break-words mt-0.5">{projectSummary.description.trim()}</p>
          ) : null}
        </div>
        <BoardViewOptions
          showDescription={showDescription}
          onDescriptionChange={setShowDescription}
          showEmptyNoColumn={showEmptyNoColumn}
          onEmptyNoColumnChange={setShowEmptyNoColumn}
          canvasMaxWidth={canvasMaxWidth}
          onCanvasMaxWidthChange={setCanvasMaxWidth}
        />
        <button
          type="button"
          onClick={openNewColumn}
          aria-label="Add column"
          title="Add column"
          className={ADD_COLUMN_BTN_CLASS}
        >
          <Plus className="h-5 w-5" strokeWidth={2} />
        </button>
      </div>

      <DndContext
        id="planner-project-board-dnd"
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={({ active }) => {
          if (String(active.id).startsWith('card-')) setActiveCardId(String(active.id));
        }}
        onDragEnd={handleUnifiedDragEnd}
      >
        <BoardHorizontalScroll
          watchKey={`${columnsOrdered.length}:${orphanSortableIds.length}:${showOrphanColumn}`}
          className="flex flex-1 min-h-0 flex-col sm:flex-row gap-3 sm:flex-nowrap sm:overflow-x-auto sm:overflow-y-hidden hide-scrollbar px-1 sm:items-stretch"
        >
          {showOrphanColumn ? (
            <BoardOrphanColumn
              orphanSortableIds={orphanSortableIds}
              cardsById={cardsById}
              onOpenNewCard={openNewCard}
              onCardClick={openEditCard}
              showDescription={showDescription}
            />
          ) : null}

          <SortableContext items={columnHeaderIds} strategy={horizontalListSortingStrategy}>
            {columnsOrdered.map((col) => {
              const key = String(col.id);
              const ids = containers[key] || [];
              return (
                <SortableBoardColumn
                  key={col.id}
                  hdrId={`colhdr-${col.id}`}
                  column={col}
                  onEditColumn={openEditColumn}
                  onDeleteColumn={deleteColumnFromMenu}
                  cardIds={ids}
                  cardsById={cardsById}
                  onCardClick={openEditCard}
                  onOpenNewCard={openNewCard}
                  showDescription={showDescription}
                />
              );
            })}
          </SortableContext>
        </BoardHorizontalScroll>

        <DragOverlay dropAnimation={null}>
          {activeCardId && cardsById[parseCardKey(activeCardId)] ? (
            <div
              className="rounded-lg border border-gray-300 shadow-lg p-3 bg-white opacity-95 w-[260px]"
              style={{
                borderLeftWidth: 4,
                borderLeftColor: cardsById[parseCardKey(activeCardId)].color,
              }}
            >
              <div className="font-medium text-sm">{cardsById[parseCardKey(activeCardId)].title}</div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <ModalColumnForm
        isOpen={columnModal.open}
        onClose={() => setColumnModal({ open: false, column: null })}
        onSubmit={submitColumn}
        initial={columnModal.column}
        submitting={columnSubmitting}
      />

      <DrawerCardForm
        isOpen={cardModal.open}
        onClose={() => setCardModal({ open: false, card: null, defaultColumnId: null })}
        onSubmit={submitCard}
        onDelete={cardModal.card ? handleDeleteCard : null}
        initial={cardModal.card}
        defaultColumnId={cardModal.defaultColumnId}
        projectId={projectId}
        columns={columnsOrdered}
        submitting={cardSubmitting}
      />

      <ConfirmModal
        isOpen={!!columnDelete}
        onClose={requestCloseColumnDelete}
        onConfirm={confirmDeleteColumn}
        title={columnDelete?.purgeCards ? 'Delete column and cards' : 'Delete column'}
        message={
          columnDelete?.purgeCards
            ? `Delete "${columnDelete.column.name}" and all cards inside it? This cannot be undone.`
            : `Delete "${columnDelete?.column?.name || ''}"? Cards will be kept under "No column".`
        }
        confirmText="Delete"
        overlayClassName="z-[60]"
      />

    </div>
  );
}
